import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const SystemIntegrationSection = () => {
  const integrations = [
    {
      icon: 'Sun',
      title: 'Zonnepanelen',
      description: 'Directe koppeling voor optimale opslag van zonne-energie',
      features: ['Automatische laadregeling', 'Overschot direct opslaan', 'Maximaal eigen verbruik']
    },
    {
      icon: 'Activity',
      title: 'Slimme meter',
      description: 'Real-time monitoring en optimalisatie van energiestromen',
      features: ['Live verbruiksinzicht', 'Automatische optimalisatie', 'Historische data']
    },
    {
      icon: 'Zap',
      title: 'Dynamisch contract',
      description: 'Automatisch laden bij lage prijzen, ontladen bij hoge prijzen',
      features: ['Prijsgestuurde besturing', 'Maximale besparing', 'API integratie']
    },
    {
      icon: 'Home',
      title: 'Smart home',
      description: 'Integratie met thuisautomatisering en energiemanagement',
      features: ['App bediening', 'Scenario\'s instellen', 'Voice control']
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Slimme systeemintegratie
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Een thuisbatterij werkt optimaal in combinatie met andere systemen
          </p>
        </motion.div>

        {/* Integration Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {integrations?.map((integration, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-xl p-6 shadow-elevation-1 hover:shadow-elevation-2 transition-all"
            >
              <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name={integration?.icon} size={24} className="text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">{integration?.title}</h3>
              <p className="text-sm text-muted-foreground mb-4">{integration?.description}</p>
              <ul className="space-y-2">
                {integration?.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Energy Flow Diagram */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 lg:p-12"
        >
          <h3 className="text-2xl font-bold text-foreground mb-8 text-center">Energiestromen in uw woning</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            {/* Solar */}
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Sun" size={40} className="text-yellow-600" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">Zonnepanelen</h4>
              <p className="text-sm text-muted-foreground">Opwekking overdag</p>
            </div>

            {/* Battery (Center) */}
            <div className="relative">
              <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-green-300 -translate-y-1/2 hidden lg:block"></div>
              <div className="relative z-10 text-center">
                <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-green-500">
                  <Icon name="Battery" size={48} className="text-green-600" />
                </div>
                <h4 className="font-semibold text-foreground mb-2">Thuisbatterij</h4>
                <p className="text-sm text-muted-foreground">Intelligente opslag</p>
              </div>
            </div>

            {/* Home */}
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Home" size={40} className="text-blue-600" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">Uw woning</h4>
              <p className="text-sm text-muted-foreground">Verbruik 24/7</p>
            </div>
          </div>

          {/* Flow Description */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="ArrowRight" size={20} className="text-green-600" />
                <span className="font-medium text-foreground">Overdag</span>
              </div>
              <p className="text-sm text-muted-foreground">Zonne-energie laadt batterij en voedt direct verbruik</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Moon" size={20} className="text-blue-600" />
                <span className="font-medium text-foreground">'s Avonds</span>
              </div>
              <p className="text-sm text-muted-foreground">Batterij voedt uw woning met opgeslagen energie</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Zap" size={20} className="text-purple-600" />
                <span className="font-medium text-foreground">Back-up</span>
              </div>
              <p className="text-sm text-muted-foreground">Bij stroomuitval neemt batterij automatisch over</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SystemIntegrationSection;